<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
    <!-- LOCATIONS -->
    <transition name="squish fade">
      <div
        v-if="isMounted"
        class="form-section group flex-h">
        <span class="icon"><icon-colored-location /></span>
        {{ capitalize($t('names.inventory', { n: 1 })) }}
        <div class="actions">
          <label
            v-show="isViewSummaryVisible"
            id="view-summary"
            for="summary-filter">
            <div class="checkbox-container">
              <input
                id="summary-filter"
                v-model="isSummaryTargetingEnabled"
                type="checkbox" />
              <span class="checkmark" />
            </div>
            <span class="group-label">{{ uppercase($t('actions.viewSummary')) }}</span>
          </label>
          <button
            class="btn btn-default btn-sm"
            @click="openFullScreenMap">
            <icon-full-screen />
            {{ capitalize($t('messages.fullscreen')) }}
          </button>
          <button
            v-if="canBeRemoved"
            class="btn btn-danger btn-sm ml-2"
            @click="clear">
            <icon-delete />
            {{ capitalize($t('actions.remove')) }}
          </button>
        </div>
      </div>
    </transition>
    <transition name="squish fade">
      <div
        v-show="isMounted"
        ref="locationSelector"
        class="location-selector"
        :class="{ 'full-screen-map': shouldDisplayFullScreenMap }">
        <overall-map
          ref="overallMap"
          :creative="creative"
          :enable-scroll-zoom="false"
          :fit-bounds-right-padding="shouldDisplayFullScreenMap ? 370 : 64"
          :enable-targeting-summary="isSummaryTargetingEnabled && !shouldDisplayFullScreenMap"
          :load-all-options-for-filters="true"
          :requested-for-u-t-c="requestedForUTC"
          @show-view-summary="(val) => (isViewSummaryVisible = val)"
          @cart-changed="onOverallMapSelectionsChanged"
          @filter-drop-down-hidden="onFilterDropDownHidden"
          @filter-drop-down-visible="onFilterDropDownVisible" />
        <button
          v-if="shouldDisplayFullScreenMap"
          id="exit_fullscreenmapInternal"
          class="btn btn-default btn-sm"
          @click="exitFullscreenMap">
          <icon-exit-full-screen />
          {{ $t('actions.exitFullscreen') }}
        </button>
      </div>
    </transition>
    <div class="mb-5" />
  </div>
</template>

<script type="text/babel">
  import { DateTime } from 'luxon';
  import { capitalize } from 'vue';
  import { mapActions } from 'vuex';

  import { IconColoredLocation } from '@/common/icons/colored';
  import { IconDelete, IconExitFullScreen, IconFullScreen } from '@/common/icons/monochrome';
  import { Creative } from '@/common/models';
  import GlobalEvents from '@/GlobalEvents';
  import { uppercase } from '@/globals';
  import OverallMap from '@/org-app/components/inventory/overallMap/OverallMap';

  export default {
    components: {
      IconDelete,
      IconColoredLocation,
      IconExitFullScreen,
      IconFullScreen,
      OverallMap,
    },
    props: {
      creative: {
        type: Creative,
        default: null,
      },
      canBeRemoved: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['changed', 'cleared', 'destroyed'],
    data() {
      return {
        isMounted: false,
        shouldDisplayFullScreenMap: false,
        isSummaryTargetingEnabled: true,
        isViewSummaryVisible: false,
      };
    },
    mounted() {
      this.isMounted = true;
      this.$emit(GlobalEvents.loaded, this);
    },
    beforeUnmount() {
      this.isMounted = false;
      this.$emit('destroyed');
    },
    computed: {
      requestedForUTC() {
        const createdOn = this.creative?.createdOn;
        return createdOn === undefined ? DateTime.utc() : createdOn;
      },
    },
    methods: {
      capitalize,
      ...mapActions([
        'updateCreativeGeofenceGroupIDs',
        'updateCreativeLocationIDs',
        'updateCreativeLocationExclusionIDs',
        'updateCreativeMarketIDs',
        'updateCreativeMarketExclusionIDs',
        'updateCreativeMediaTypeIDs',
        'updateCreativeMediaTypeExclusionIDs',
        'updateCreativeNetworkIDs',
        'updateCreativeNetworkExclusionIDs',
        'updateCreativeSiteIDs',
        'updateCreativeSiteExclusionIDs',
        'updateCreativeUnitIDs',
        'updateCreativeUnitExclusionIDs',
        'updateCreativeUnitTags',
        'updateCreativeUnitTagExclusions',
        'updateCreativeCreativeFormats',
        'updateCreativeCreativeFormatExclusions',
      ]),
      uppercase,
      onOverallMapSelectionsChanged() {
        if (this.onOverallMapSelectionsChangedTimer != null) {
          clearTimeout(this.onOverallMapSelectionsChangedTimer);
        }
        this.onOverallMapSelectionsChangedTimer = setTimeout(() => {
          this.onOverallMapSelectionsChangedTimer = null;
          if (this.$refs.estimatedDeliveryStats != null) {
            this.$refs.estimatedDeliveryStats.refresh();
          }
        }, 200);
      },
      openFullScreenMap() {
        this.shouldDisplayFullScreenMap = true;
        this.$refs.overallMap.enableMouseWheel();
        let count = 10;
        const timer = setInterval(() => {
          this.$refs.overallMap.map.resize();
          count -= 1;
          if (count === 0) {
            clearInterval(timer);
          }
        }, 100);
      },
      exitFullscreenMap() {
        this.shouldDisplayFullScreenMap = false;
        this.$refs.overallMap.disableMouseWheel();
      },
      onFilterDropDownHidden() {
        // this.isEstimatedDeliveryStatsVisible = true;
      },
      onFilterDropDownVisible() {
        if (this.shouldDisplayFullScreenMap) {
          // this.isEstimatedDeliveryStatsVisible = false;
        }
      },
      clear() {
        this.updateCreativeLocationIDs({ creative: this.creative, value: [] });
        this.updateCreativeLocationExclusionIDs({ creative: this.creative, value: [] });
        this.updateCreativeGeofenceGroupIDs({ creative: this.creative, value: [] });
        this.updateCreativeMarketIDs({ creative: this.creative, value: [] });
        this.updateCreativeMarketExclusionIDs({ creative: this.creative, value: [] });
        this.updateCreativeMediaTypeIDs({ creative: this.creative, value: [] });
        this.updateCreativeMediaTypeExclusionIDs({ creative: this.creative, value: [] });
        this.updateCreativeNetworkIDs({ creative: this.creative, value: [] });
        this.updateCreativeNetworkExclusionIDs({ creative: this.creative, value: [] });
        this.updateCreativeSiteIDs({ creative: this.creative, value: [] });
        this.updateCreativeSiteExclusionIDs({ creative: this.creative, value: [] });
        this.updateCreativeUnitIDs({ creative: this.creative, value: [] });
        this.updateCreativeUnitExclusionIDs({ creative: this.creative, value: [] });
        this.updateCreativeUnitTags({ creative: this.creative, value: [] });
        this.updateCreativeUnitTagExclusions({ creative: this.creative, value: [] });
        this.updateCreativeCreativeFormats({ creative: this.creative, value: [] });
        this.updateCreativeCreativeFormatExclusions({ creative: this.creative, value: [] });
        this.$emit('cleared');
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss">
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  .location-selector {
    transition: all 1s cubic-bezier(0.2, 1, 0.2, 1);
    border: solid 0.1rem $borders;
    display: flex;

    #exit_fullscreenmapInternal {
      display: none;
      position: fixed;
      top: 7.4rem;
      right: 5rem;
    }

    &.full-screen-map {
      transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      position: fixed;
      z-index: 1;
      top: 6.4rem;
      right: 0;
      left: 0;
      bottom: 0;
      background: #fff;

      #exit_fullscreenmapInternal {
        display: block;
        z-index: 2;
      }

      .overview-map-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        height: auto;
      }
    }

    .overview-map-container {
      height: 600px;
      height: 60vh;
    }
  }
</style>
